<template>
    <div
        style="height: 90vh"
        class="leo-flex leo-flex-col desktop:leo-h-full leo-m-0 leo-m-auto leo-py-xl desktop:leo-py-xs tablet:leo-h-full tablet:leo-py-xs"
    >
        <overlay
            :isLoading="isLoading"
            :displayWhiteBackground="displayWhiteBackground"
        ></overlay>
        <template>
            <div
                v-if="isRedirecting"
                class="leo-fixed leo-top-0 leo-left-0 leo-right-0 leo-bottom-0 leo-w-full leo-h-screen leo-z-5 leo-overflow-hidden leo-bg-grey-600 leo-opacity-75 leo-flex leo-flex-col leo-items-center leo-justify-center"
            >
                <leo-loader />
            </div>
        </template>
        <div
            class="leo-flex leo-flex-col leo-gap-xl desktop:leo-gap-md leo-flex-grow"
        >
            <slot name="progress-stepper"></slot>
            <!-- <alert /> -->
            <div
                class="leo-text-lg leo-text-secondary leo-font-semibold"
                :class="headingClass"
            >
                {{ heading }}
            </div>
            <slot name="confirmation-text"></slot>
            <div v-if="imageType" class="leo-justify-items-center">
                <home-page v-if="imageType === 'homepage'"></home-page>
                <protection v-else-if="imageType === 'protection'"></protection>
                <signout v-else-if="imageType === 'sign-out'"></signout>
                <missing-info
                    v-else-if="imageType === 'missing-info'"
                ></missing-info>
                <successful-fund-switch
                    v-else-if="imageType === 'successful-fund-switch'"
                ></successful-fund-switch>
                <member-id v-else-if="imageType === 'member-id'"></member-id>
                <policy-no-fund
                    v-else-if="imageType === 'policy-no-fund'"
                ></policy-no-fund>
                <update-myinfo
                    v-else-if="imageType === 'update-myinfo'"
                ></update-myinfo>
                <leo-illustration v-else :imageType="imageType" width="100%" />
            </div>
            <div
                v-if="!isSubHeadingHtml && subheading"
                class="leo-text-md leo-text-accent break-word"
            >
                {{ subheading }}
            </div>
            <div
                v-else-if="isSubHeadingHtml && subheading"
                v-html="sanitizedHtml(subheading)"
                class="leo-text-md leo-text-accent break-word"
            ></div>
            <slot />
        </div>
    </div>
</template>
<script>
import Overlay from "@/components/shared/overlay/Overlay"
import { VueLeoLoader } from "@greateasternlife/leo-loader"
import sanitizeHtml from "sanitize-html"

import HomePageVue from "./HomePage.vue"
import ProtectionVue from "./Protection.vue"
import SignoutVue from "./Signout.vue"
const HTML_SANITIZE_DOMAIN= {
    DOMAIN: 'greateasternlife.com',
    DOT_DOMAIN: '.greateasternlife.com'
}
export default {
    name: "DetailCard",
    components: {
        overlay: Overlay,
        "leo-loader": VueLeoLoader,
        "home-page": HomePageVue,
        protection: ProtectionVue,
        signout: SignoutVue,
        "missing-info": () => import("./MissingInfo.vue"),
        "successful-fund-switch": () => import("./SuccessfulFundSwitch.vue"),
        "member-id": () => import("./MemberId.vue"),
        "policy-no-fund": () => import("./PolicyNoFund.vue"),
        "update-myinfo": () => import("./UpdateMyInfo.vue"),
        "leo-illustration": () => import("./Leo-illustration.vue"),
    },
    props: {
        heading: {
            type: String,
            default: null,
        },
        imageType: {
            type: String,
            default: null,
        },
        subheading: {
            type: String,
            default: null,
        },
        headingClass: {
            type: String,
            default: "leo-text-center",
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        isRedirecting: {
            type: Boolean,
            default: false,
        },
        isSubHeadingHtml: {
            type: Boolean,
            default: false,
        },
        displayWhiteBackground: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {}
    },  
    methods: {
        //common method to validate url, src, srcet for html
        isSafeSrcsetUrl(urlMatch) {
            const allowedSchemes = ['http:', 'https:'] // ✅ Allowed protocols

            const unsafeSchemesRegex =
                /^(javascript|data|vbscript|livescript|file|filesystem|about|blob|ws|wss|ftp|chrome|chrome-extension|moz-extension|ms-browser-extension|intent|itms|itms-apps|skype|whatsapp|tg|steam|discord|mailto|tel|sms):/i

            const xssPatterns = [
                /<script\b[^>]*>/i,
                /<\/script>/i,
                /javascript:/i,
                /on\w+\s*=/i,
                /document\./i,
                /window\./i,
                /eval\s*\(/i,
                /expression\s*\(/i,
                /-moz-binding/i,
                /data:/i,
                /vbscript:/i,
                /<iframe\b[^>]*>/i,
                /<svg\b[^>]*onload=/i,
                /<img\b[^>]*onerror=/i
            ]

            try {
                for (const url of urlMatch) {
                    const sanitizedUrl = url.replace(/[\n\r]/g, '').trim()
                    const parsedUrl = new URL(sanitizedUrl)
                    const normalizedHostname = parsedUrl.hostname.replace(/^www\./, '')

                    const isValidDomain =
                        normalizedHostname === HTML_SANITIZE_DOMAIN.DOMAIN || normalizedHostname.endsWith(HTML_SANITIZE_DOMAIN.DOT_DOMAIN)

                    const isValidSchemes = allowedSchemes.includes(parsedUrl.protocol.toLowerCase()) && !unsafeSchemesRegex.test(parsedUrl.protocol.toLowerCase())

                    const isMaliciousSearchQuery = [...parsedUrl.searchParams.values()].some((query) => {
                        const decodedQuery = decodeURIComponent(query).toLowerCase()
                        return xssPatterns.some((pattern) => pattern.test(decodedQuery))
                    })

                    if (!isValidDomain || !isValidSchemes || isMaliciousSearchQuery) {
                        return false
                    }
                }
                return true
            } catch (e) {
                // If parsing fails, it's unsafe
                return false
            }
        },
        sanitizedHtml(htmlContent) {
            return sanitizeHtml(htmlContent, {
                allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img']), // No <script> tag allowed
                allowedAttributes: {
                    a: [],
                    img: ['src', 'srcset', 'alt', 'title', 'width', 'height', 'loading', 'class', 'style']
                },
                img: (tagName, attribs) => {
					let src = ''
					if (attribs.src) {
						// Extract url passed from onclick
						const urlMatch = [attribs.src]

						let isValidSrc = true
						try {
							isValidSrc = this.isSafeSrcsetUrl(urlMatch)
						} catch (err) {
							isValidSrc = false
						}

						if (isValidSrc) {
							src = attribs.src
						}
					}

					let srcset = ''

					if (attribs.srcset) {
						// Extract url passed from onclick
						const urlMatch = attribs.srcset.split(',')

						let isValidSrcSet = true
						try {
							isValidSrcSet = this.isSafeSrcsetUrl(urlMatch)
						} catch (err) {
							isValidSrcSet = false
						}

						if (isValidSrcSet) {
							srcset = attribs.srcset
						}
					}

					return {
						tagName: 'img',
						attribs: { ...attribs, src, srcset } // Set onclick attribs to blank if unsafe
					}
				}
            })
        },
    },
}
</script>
<style scoped>
.break-word {
    word-break: break-word;
}
</style>
